import { BasketItem } from "../../models/BasketItem";
import { Action } from "../../models/Action";
import { SelectedSeats } from "../../models/SelectedSeats";
import { QntStateItem } from "../../models/QntStateItem";
import {
  addBasketItem,
  addLimitedBasketItem,
  clearBasket,
  fetchBasket,
  prolongBasketPass,
  resetBasket,
  setBasketAddError,
  setCurrentPromoCode,
  setPromoMessage,
} from "./basketActions";
import { UPDATE_BASKET_LENGTH, UPDATE_CALENDAR_SALE_QNT_STATE, UPDATE_SELECT_SEATS } from "./basketTypes";

export interface BasketState {
  loading: boolean;
  selectedSeats: SelectedSeats[];
  basketLength: number;
  basketTotal: number;
  calendarSaleQntState: QntStateItem[];
  items: BasketItem[];
  totalListPrice: number;
  promoMessage: string;
  currentPromoCode: string;
  addError: string;
  hasFetched: boolean;
}

const initialBasket: BasketState = {
  loading: false,
  selectedSeats: [],
  basketLength: 0,
  basketTotal: 0,
  calendarSaleQntState: [],
  items: [],
  totalListPrice: 0,
  promoMessage: "",
  currentPromoCode: "",
  addError: "",
  hasFetched: false,
};

type BasketAction = Action<any> & { meta?: { arg?: { skipLoading?: boolean } } };

const skipsLoading = (action: BasketAction) => Boolean(action.meta?.arg?.skipLoading);

const countItems = (items: BasketItem[]) => items.reduce((acc, item) => acc + 1 * item.qnt, 0);

const sumItems = (items: BasketItem[]) => items.reduce((acc, item) => acc + item.price * item.qnt, 0);

const readBasketPayload = (payload: any): { items: BasketItem[]; totalListPrice: number } => {
  const body = payload && Array.isArray(payload.basket) ? payload : payload?.result;
  const items = Array.isArray(body?.basket) ? body.basket : [];
  return {
    items,
    totalListPrice: body?.totalListPrice ?? 0,
  };
};

const withBasket = (basketState: BasketState, payload: any, loading: boolean): BasketState => {
  const { items, totalListPrice } = readBasketPayload(payload);
  return {
    ...basketState,
    items,
    totalListPrice,
    basketTotal: sumItems(items),
    basketLength: countItems(items),
    hasFetched: true,
    loading,
  };
};

export default function basketReducer(basketState = initialBasket, action: BasketAction) {
  switch (action.type) {
    case UPDATE_SELECT_SEATS:
      return {
        ...basketState,
        selectedSeats: action.payload,
      };
    case UPDATE_BASKET_LENGTH:
      return {
        ...basketState,
        basketLength: action.payload,
      };
    case UPDATE_CALENDAR_SALE_QNT_STATE:
      return {
        ...basketState,
        calendarSaleQntState: action.payload,
      };
    case setPromoMessage.toString():
      return {
        ...basketState,
        promoMessage: action.payload,
      };
    case setBasketAddError.toString():
      return {
        ...basketState,
        addError: action.payload,
      };
    case setCurrentPromoCode.toString():
      return {
        ...basketState,
        currentPromoCode: action.payload,
      };
    case fetchBasket.pending.toString():
      if (skipsLoading(action)) {
        return basketState;
      }
      return {
        ...basketState,
        loading: true,
      };
    case fetchBasket.fulfilled.toString(): {
      if (action.payload?.ignored) {
        if (skipsLoading(action)) {
          return basketState;
        }
        return {
          ...basketState,
          loading: false,
        };
      }
      const body =
        action.payload && Array.isArray(action.payload.basket) ? action.payload : action.payload?.result;
      if (!body || !Array.isArray(body.basket)) {
        if (skipsLoading(action)) {
          return basketState;
        }
        return {
          ...basketState,
          loading: false,
        };
      }
      return withBasket(basketState, action.payload, skipsLoading(action) ? basketState.loading : false);
    }
    case fetchBasket.rejected.toString():
      if (skipsLoading(action)) {
        return basketState;
      }
      return {
        ...basketState,
        loading: false,
      };
    case addBasketItem.pending.toString():
      return {
        ...basketState,
        loading: true,
        addError: "",
      };
    case addLimitedBasketItem.pending.toString():
    case prolongBasketPass.pending.toString():
      return {
        ...basketState,
        addError: "",
      };
    case addBasketItem.fulfilled.toString():
    case addBasketItem.rejected.toString():
      return {
        ...basketState,
        loading: false,
      };
    case clearBasket.fulfilled.toString():
    case resetBasket.toString():
      return {
        ...basketState,
        items: [],
        basketTotal: 0,
        totalListPrice: 0,
        basketLength: 0,
        promoMessage: "",
        currentPromoCode: "",
        addError: "",
      };
    default:
      return basketState;
  }
}
